import { adminApiClient } from '@/services/admin/baseService'
import { getApiClientHeaderForMode } from '@/utils/apiConfig'
import type {
  ApiResponse,
  PlatformWalletBalancesMonitorSnapshot,
  PlatformWalletSnapshot,
  RpcHealthSnapshot,
} from '@/types'
import axios, { type AxiosRequestConfig } from 'axios'

type JsonMap = Record<string, unknown>
type NetworkMode = 'mainnet' | 'testnet'

export interface RuntimeConfigItem {
  id: string
  isTestnet: boolean
  configJson: JsonMap
  version: string
  createdAt: string
  updatedAt: string
}

export interface RuntimeConfigUpdateResult {
  item: RuntimeConfigItem | null
  noOp: boolean
  cacheDegraded: boolean
  auditDegraded: boolean
}

export interface SystemRpcEndpointTemplate {
  id: string
  isTestnet: boolean
  chainCode: string
  endpointKey: string
  urlTemplate: string
  provider: string
  priority: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface SystemPlatformWallet {
  id: string
  isTestnet: boolean
  compatibility: string
  role: string
  address: string
  isActive: boolean
  notes: string | null
  createdAt: string
  updatedAt: string
}

export interface SystemChain {
  id: string
  chainCode: string
  name: string
  scanBaseUrl: string
  compatibility: string
  chainId: number
  nativeCurrencyName: string
  nativeCurrencySymbol: string
  nativeCurrencyDecimals: number
  confirmationBlocks: number
  finalityOffsetMainnet: number | null
  finalityOffsetTestnet: number | null
  orderAmountHardFloorUsd: number
  isTestnet: boolean
  isActive: boolean
  allowNewOrders: boolean
  createdAt: string
  updatedAt: string
}

export interface SystemToken {
  id: string
  symbol: string
  name: string
  chainId: string
  chainCode: string
  chainName: string
  chainCompatibility: string
  contractAddress: string
  decimals: number
  isActive: boolean
  allowNewOrders: boolean
  isTestnet: boolean
  createdAt: string
  updatedAt: string
}

export interface OrderFeePolicy {
  id: string
  name: string
  status: 'draft' | 'active' | 'archived'
  version: string
  effectiveFrom: string | null
  effectiveTo: string | null
  parentPolicyId: string | null
  isTestnet: boolean
  planId?: string | null
  tierLevel?: number | null
  planName?: string | null
  createdAt: string
  updatedAt: string
}

export interface OrderFeePolicyChainRate {
  id: string
  policyId: string
  chainCode: string
  feeRateBps: number
  minFeeUsd: number
  maxFeeUsd: number
  isTestnet: boolean
  createdAt: string
  updatedAt: string
}

export interface OrderFeePolicyDetail {
  policy: OrderFeePolicy
  chainRates: OrderFeePolicyChainRate[]
}

export interface CacheRefreshResult {
  status?: string
  mode?: 'mainnet' | 'testnet'
  refreshed?: number
  failed?: number
  requestedAt?: string
  finishedAt?: string
  key?: string
  version?: string
}

export interface SystemSubscriptionPlanTier {
  id: string
  planId: string
  tierLevel: number
  minMonthlyVolumeUsd: number
  maxMonthlyVolumeUsd: number | null
  minSettledOrderCount: number
  createdAt: string
  updatedAt: string
}

export interface SystemSubscriptionPlan {
  id: string
  name: string
  displayName: string
  description: string | null
  visibility: 'public' | 'internal'
  monthlyFeeUsd: number
  subscriptionPricingJson?: string
  contractsDeployQuotaPerPeriod: number
  orderCreateQuotaPerPeriod: number
  maxLiveOrders: number
  maxMerchantCount: number
  maxApiKeysPerMerchant: number
  merchantConfigPolicyJson: string
  capabilitiesJson: string
  priorityLevel: 'low' | 'normal' | 'high'
  hasUsageTiers: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  tiers: SystemSubscriptionPlanTier[]
}

export interface SystemSubscriptionPlanPayload {
  name?: string
  displayName?: string
  description?: string | null
  visibility?: 'public' | 'internal'
  monthlyFeeUsd?: number
  subscriptionPricingJson?: string
  contractsDeployQuotaPerPeriod?: number
  orderCreateQuotaPerPeriod?: number
  maxLiveOrders?: number
  maxMerchantCount?: number
  maxApiKeysPerMerchant?: number
  merchantConfigPolicyJson?: string
  capabilitiesJson?: string
  priorityLevel?: 'low' | 'normal' | 'high'
  hasUsageTiers?: boolean
  isActive?: boolean
}

export interface SystemSubscriptionPlanTierPayload {
  tierLevel?: number
  minMonthlyVolumeUsd?: number
  maxMonthlyVolumeUsd?: number | null
  minSettledOrderCount?: number
}

const extractPayload = (response: { data?: any }): any => response?.data ?? null

const extractItem = <T>(response: { data?: any }): T | null => {
  const payload = extractPayload(response)
  return (payload?.item ?? payload?.data?.item ?? payload?.data ?? null) as T | null
}

const extractItems = <T>(response: { data?: any }): T[] => {
  const payload = extractPayload(response)
  if (Array.isArray(payload?.items)) {
    return payload.items as T[]
  }
  if (Array.isArray(payload?.data?.items)) {
    return payload.data.items as T[]
  }
  return []
}

type ModeHeaderRequestConfig = AxiosRequestConfig & {
  __preserveAppClientHeader: true
}

const withModeHeaders = (
  mode?: NetworkMode
): ModeHeaderRequestConfig | undefined => {
  if (!mode) {
    return undefined
  }

  return {
    headers: {
      'X-App-Client': getApiClientHeaderForMode(mode),
    },
    __preserveAppClientHeader: true as const,
  }
}

const resolveApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { error?: unknown; message?: unknown }
      | undefined
    const errorMessage = typeof data?.error === 'string' ? data.error.trim() : ''
    if (errorMessage) {
      return errorMessage
    }
    const message = typeof data?.message === 'string' ? data.message.trim() : ''
    if (message) {
      return message
    }
    if (typeof error.message === 'string' && error.message.trim().length > 0) {
      return error.message.trim()
    }
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message.trim()
  }
  return fallback
}

export const systemService = {
  async getSystemStatus(mode?: NetworkMode): Promise<any> {
    try {
      const response = await adminApiClient.get<ApiResponse<any>>(
        '/admin/system/status',
        withModeHeaders(mode)
      )
      if (response?.data?.data) {
        return response.data.data
      }

      throw new Error('System status API returned no data')
    } catch (error) {
      console.error('Error fetching system status:', error)
      const apiMessage =
        (error as any)?.response?.data?.error?.message ??
        (error as any)?.response?.data?.message ??
        (error instanceof Error ? error.message : null)
      throw new Error(apiMessage || 'Failed to fetch system status')
    }
  },

  async getRpcHealth(mode?: NetworkMode): Promise<RpcHealthSnapshot[]> {
    try {
      const response = await adminApiClient.get<ApiResponse<RpcHealthSnapshot[]>>(
        '/admin/system/rpc/health',
        withModeHeaders(mode)
      )
      if (response?.data?.data) {
        return response.data.data
      }
    } catch (error) {
      console.error('Error fetching RPC health snapshot:', error)
    }
    return []
  },

  async updateRpcPriority(
    payload: { chainKey: string; endpointOrder?: string[]; reset?: boolean },
    mode?: NetworkMode
  ): Promise<RpcHealthSnapshot[]> {
    const body = {
      chainKey: payload.chainKey,
      endpointOrder: payload.endpointOrder,
      reset: payload.reset ?? false,
    }

    try {
      const response = await adminApiClient.post<ApiResponse<RpcHealthSnapshot[]>>(
        '/admin/system/rpc/priority',
        body,
        withModeHeaders(mode)
      )
      return response?.data?.data ?? []
    } catch (error) {
      console.error('Error updating RPC priority:', error)
      throw error
    }
  },

  async updateRpcPriorityBulk(
    payload: { mode: 'reset' } | { mode: 'reorder'; providerOrder: string[] },
    mode?: NetworkMode
  ): Promise<RpcHealthSnapshot[]> {
    try {
      const response = await adminApiClient.post<ApiResponse<RpcHealthSnapshot[]>>(
        '/admin/system/rpc/priority/bulk',
        payload,
        withModeHeaders(mode)
      )
      return response?.data?.data ?? []
    } catch (error) {
      console.error('Error updating RPC priority (bulk):', error)
      throw error
    }
  },

  async updateRpcEndpointStatus(
    payload: { chainKey: string; endpointId: string; disabled: boolean },
    mode?: NetworkMode
  ): Promise<RpcHealthSnapshot[]> {
    try {
      const response = await adminApiClient.post<ApiResponse<RpcHealthSnapshot[]>>(
        '/admin/system/rpc/status',
        payload,
        withModeHeaders(mode)
      )
      return response?.data?.data ?? []
    } catch (error) {
      console.error('Error updating RPC endpoint status:', error)
      throw error
    }
  },

  async getPlatformWalletSnapshot(params?: {
    evmAddress?: string
    tronAddress?: string
    solanaAddress?: string
  }, mode?: NetworkMode): Promise<PlatformWalletSnapshot | null> {
    const address = params?.evmAddress?.trim()
    const tronAddress = params?.tronAddress?.trim()
    const solanaAddress = params?.solanaAddress?.trim()

    try {
      const response = await adminApiClient.get<ApiResponse<PlatformWalletSnapshot>>(
        '/admin/monitor/platform-wallet',
        {
          params: {
            address: address && address.length > 0 ? address : undefined,
            tronAddress: tronAddress && tronAddress.length > 0 ? tronAddress : undefined,
            solanaAddress: solanaAddress && solanaAddress.length > 0 ? solanaAddress : undefined,
          },
          ...withModeHeaders(mode),
        }
      )

      return response?.data?.data ?? null
    } catch (error) {
      const message = resolveApiErrorMessage(error, 'Failed to fetch platform wallet snapshot')
      console.error('Error fetching platform wallet snapshot:', {
        message,
        error,
      })
      throw new Error(message)
    }
  },

  async getPlatformWalletBalances(
    mode?: NetworkMode
  ): Promise<PlatformWalletBalancesMonitorSnapshot | null> {
    try {
      const response = await adminApiClient.get<
        ApiResponse<PlatformWalletBalancesMonitorSnapshot>
      >(
        '/admin/monitor/platform-wallet-balances',
        withModeHeaders(mode)
      )
      return response?.data?.data ?? null
    } catch (error) {
      const message = resolveApiErrorMessage(
        error,
        'Failed to fetch platform wallet balances'
      )
      console.error('Error fetching platform wallet balances:', {
        message,
        error,
      })
      throw new Error(message)
    }
  },

  async getRuntimeConfigCurrent(mode?: NetworkMode): Promise<RuntimeConfigItem | null> {
    const response = await adminApiClient.get(
      '/admin/system/runtime-config/current',
      withModeHeaders(mode)
    )
    return extractItem<RuntimeConfigItem>(response)
  },

  async updateRuntimeConfigCurrent(
    payload: { configJson: JsonMap; expectedUpdatedAt: string },
    mode?: NetworkMode
  ): Promise<RuntimeConfigUpdateResult> {
    const response = await adminApiClient.put(
      '/admin/system/runtime-config/current',
      {
        configJson: payload.configJson,
        expectedUpdatedAt: payload.expectedUpdatedAt,
      },
      withModeHeaders(mode)
    )
    const responsePayload = extractPayload(response)
    const result = responsePayload?.data ?? responsePayload
    return {
      item: (result?.item ?? null) as RuntimeConfigItem | null,
      noOp: result?.noOp === true,
      cacheDegraded: result?.cacheDegraded === true,
      auditDegraded: result?.auditDegraded === true,
    }
  },

  async refreshRuntimeConfigCache(mode?: NetworkMode): Promise<CacheRefreshResult> {
    const response = await adminApiClient.post(
      '/admin/system/runtime-config/cache/refresh',
      undefined,
      withModeHeaders(mode)
    )
    return extractPayload(response) as CacheRefreshResult
  },

  async getSystemRpcEndpoints(params?: {
    chainCode?: string
  }, mode?: NetworkMode): Promise<SystemRpcEndpointTemplate[]> {
    const response = await adminApiClient.get('/admin/system/rpc-endpoints', {
      params: {
        chainCode: params?.chainCode?.trim() || undefined,
      },
      ...withModeHeaders(mode),
    })
    return extractItems<SystemRpcEndpointTemplate>(response)
  },

  async updateSystemRpcEndpoint(
    id: string,
    payload: {
      urlTemplate?: string
      provider?: string
      priority?: number
      isActive?: boolean
    },
    mode?: NetworkMode
  ): Promise<SystemRpcEndpointTemplate | null> {
    const response = await adminApiClient.put(
      `/admin/system/rpc-endpoints/${encodeURIComponent(id)}`,
      payload,
      withModeHeaders(mode)
    )
    return extractItem<SystemRpcEndpointTemplate>(response)
  },

  async refreshSystemRpcEndpointsCache(mode?: NetworkMode): Promise<CacheRefreshResult> {
    const response = await adminApiClient.post(
      '/admin/system/rpc-endpoints/cache/refresh',
      undefined,
      withModeHeaders(mode)
    )
    return extractPayload(response) as CacheRefreshResult
  },

  async getSystemPlatformWallets(mode?: NetworkMode): Promise<SystemPlatformWallet[]> {
    const response = await adminApiClient.get(
      '/admin/system/platform-wallets',
      withModeHeaders(mode)
    )
    return extractItems<SystemPlatformWallet>(response)
  },

  async updateSystemPlatformWallet(
    id: string,
    payload: {
      address?: string
      isActive?: boolean
      notes?: string | null
    },
    mode?: NetworkMode
  ): Promise<SystemPlatformWallet | null> {
    const response = await adminApiClient.put(
      `/admin/system/platform-wallets/${encodeURIComponent(id)}`,
      payload,
      withModeHeaders(mode)
    )
    return extractItem<SystemPlatformWallet>(response)
  },

  async refreshSystemPlatformWalletsCache(mode?: NetworkMode): Promise<CacheRefreshResult> {
    const response = await adminApiClient.post(
      '/admin/system/platform-wallets/cache/refresh',
      undefined,
      withModeHeaders(mode)
    )
    return extractPayload(response) as CacheRefreshResult
  },

  async getSystemChains(mode?: NetworkMode): Promise<SystemChain[]> {
    const response = await adminApiClient.get('/admin/system/chains', withModeHeaders(mode))
    return extractItems<SystemChain>(response)
  },

  async updateSystemChain(
    id: string,
    payload: {
      name?: string
      scanBaseUrl?: string
      confirmationBlocks?: number
      finalityOffsetMainnet?: number | null
      finalityOffsetTestnet?: number | null
      orderAmountHardFloorUsd?: number
      isActive?: boolean
      allowNewOrders?: boolean
    },
    mode?: NetworkMode
  ): Promise<SystemChain | null> {
    const response = await adminApiClient.patch(
      `/admin/system/chains/${encodeURIComponent(id)}`,
      payload,
      withModeHeaders(mode)
    )
    return extractItem<SystemChain>(response)
  },

  async refreshSystemChainsCache(mode?: NetworkMode): Promise<CacheRefreshResult> {
    const response = await adminApiClient.post(
      '/admin/system/chains/cache/refresh',
      undefined,
      withModeHeaders(mode)
    )
    return extractPayload(response) as CacheRefreshResult
  },

  async getSystemTokens(params?: {
    chainId?: string
    symbol?: string
  }, mode?: NetworkMode): Promise<SystemToken[]> {
    const response = await adminApiClient.get('/admin/system/tokens', {
      params: {
        chainId: params?.chainId?.trim() || undefined,
        symbol: params?.symbol?.trim() || undefined,
      },
      ...withModeHeaders(mode),
    })
    return extractItems<SystemToken>(response)
  },

  async updateSystemToken(
    id: string,
    payload: {
      name?: string
      contractAddress?: string
      decimals?: number
      isActive?: boolean
      allowNewOrders?: boolean
    },
    mode?: NetworkMode
  ): Promise<SystemToken | null> {
    const response = await adminApiClient.patch(
      `/admin/system/tokens/${encodeURIComponent(id)}`,
      payload,
      withModeHeaders(mode)
    )
    return extractItem<SystemToken>(response)
  },

  async refreshSystemTokensCache(mode?: NetworkMode): Promise<CacheRefreshResult> {
    const response = await adminApiClient.post(
      '/admin/system/tokens/cache/refresh',
      undefined,
      withModeHeaders(mode)
    )
    return extractPayload(response) as CacheRefreshResult
  },

  async getOrderFeePolicies(mode?: NetworkMode): Promise<OrderFeePolicy[]> {
    const response = await adminApiClient.get(
      '/admin/system/order-fee-policies',
      withModeHeaders(mode)
    )
    return extractItems<OrderFeePolicy>(response)
  },

  async getOrderFeePolicyDetail(
    policyId: string,
    mode?: NetworkMode
  ): Promise<OrderFeePolicyDetail | null> {
    const response = await adminApiClient.get(
      `/admin/system/order-fee-policies/${encodeURIComponent(policyId)}`,
      withModeHeaders(mode)
    )
    return extractItem<OrderFeePolicyDetail>(response)
  },

  async refreshOrderFeePolicyCache(mode?: NetworkMode): Promise<CacheRefreshResult> {
    const response = await adminApiClient.post(
      '/admin/system/order-fee-policies/cache/refresh',
      undefined,
      withModeHeaders(mode)
    )
    return extractPayload(response) as CacheRefreshResult
  },

  async createOrderFeePolicy(payload: {
    parentPolicyId?: string | null
    version: string
    name?: string | null
    activate?: boolean
    chainRates: Array<{
      chainCode: string
      feeRateBps: number
      minFeeUsd: number
      maxFeeUsd: number
    }>
  }, mode?: NetworkMode): Promise<OrderFeePolicyDetail | null> {
    const response = await adminApiClient.post(
      '/admin/system/order-fee-policies',
      payload,
      withModeHeaders(mode)
    )
    return extractItem<OrderFeePolicyDetail>(response)
  },

  async updateOrderFeePolicy(
    policyId: string,
    payload: {
      name?: string | null
      status?: 'draft' | 'active' | 'archived'
      activatePolicyId?: string | null
      version?: string | null
      effectiveFrom?: string | null
      effectiveTo?: string | null
      chainRates?: Array<{
      chainCode: string
      feeRateBps: number
      minFeeUsd: number
      maxFeeUsd: number
    }>
    },
    mode?: NetworkMode
  ): Promise<OrderFeePolicyDetail | null> {
    const response = await adminApiClient.patch(
      `/admin/system/order-fee-policies/${encodeURIComponent(policyId)}`,
      payload,
      withModeHeaders(mode)
    )
    return extractItem<OrderFeePolicyDetail>(response)
  },

  async getSubscriptionPlans(mode?: NetworkMode): Promise<SystemSubscriptionPlan[]> {
    const response = await adminApiClient.get(
      '/admin/system/subscription-plans',
      withModeHeaders(mode)
    )
    return extractItems<SystemSubscriptionPlan>(response)
  },

  async refreshSubscriptionPlanCache(mode?: NetworkMode): Promise<CacheRefreshResult> {
    const response = await adminApiClient.post(
      '/admin/system/subscription-plans/cache/refresh',
      undefined,
      withModeHeaders(mode)
    )
    return extractPayload(response) as CacheRefreshResult
  },

  async createSubscriptionPlan(
    payload: SystemSubscriptionPlanPayload,
    mode?: NetworkMode
  ): Promise<SystemSubscriptionPlan | null> {
    const response = await adminApiClient.post(
      '/admin/system/subscription-plans',
      payload,
      withModeHeaders(mode)
    )
    return extractItem<SystemSubscriptionPlan>(response)
  },

  async updateSubscriptionPlan(
    planId: string,
    payload: SystemSubscriptionPlanPayload,
    mode?: NetworkMode
  ): Promise<SystemSubscriptionPlan | null> {
    const response = await adminApiClient.patch(
      `/admin/system/subscription-plans/${encodeURIComponent(planId)}`,
      payload,
      withModeHeaders(mode)
    )
    return extractItem<SystemSubscriptionPlan>(response)
  },

  async createSubscriptionPlanTier(
    planId: string,
    payload: SystemSubscriptionPlanTierPayload,
    mode?: NetworkMode
  ): Promise<SystemSubscriptionPlanTier | null> {
    const response = await adminApiClient.post(
      `/admin/system/subscription-plans/${encodeURIComponent(planId)}/tiers`,
      payload,
      withModeHeaders(mode)
    )
    return extractItem<SystemSubscriptionPlanTier>(response)
  },

  async updateSubscriptionPlanTier(
    planId: string,
    tierId: string,
    payload: SystemSubscriptionPlanTierPayload,
    mode?: NetworkMode
  ): Promise<SystemSubscriptionPlanTier | null> {
    const response = await adminApiClient.patch(
      `/admin/system/subscription-plans/${encodeURIComponent(planId)}/tiers/${
        encodeURIComponent(tierId)
      }`,
      payload,
      withModeHeaders(mode)
    )
    return extractItem<SystemSubscriptionPlanTier>(response)
  },
}
